/**
 * Schémas Zod — Comparison foundation (Chunk 9.Q.1 — matrice brute read-only).
 *
 * Source DB (lecture seule, aucune migration) :
 *   - `app.submissions` (colonnes : insurer_code, insurer_name, status, project_id, quote_request_id)
 *   - `app.extracted_fields` (field_key, value, unit, confidence, agreement, needs_review)
 *   - `app.anomalies` (field_keys[], resolved_at) — overlay open anomalies
 *
 * Matrice : lignes = `field_key` canoniques alignés cross-submissions
 * (`unique(submission_id, field_key)`) ; colonnes = submissions (1 par assureur,
 * `unique(project_id, insurer_code)`) ; cellules = valeur consolidée + flags.
 *
 * Phase 1 read-only. Anti-leak : aucun citation_*, source_model_id, override_*,
 * extraction_id, structure_hash, flags, notes, detection_confidence exposé.
 * Couches normalisation / notes / rapport client (ADR 0026) = chunks ultérieurs.
 */

import { z } from "zod";

import * as Comparison from "../comparison/index.js";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Colonne — submission (assureur)
// -----------------------------------------------------------------------------

/**
 * Passe métier dont la DERNIÈRE tentative a échoué **sans produire aucun champ**
 * — chunk 9.AB.1, décision PO option B.
 *
 * Pourquoi ce signal existe au niveau de la SOUMISSION et pas dans la matrice :
 * une passe qui échoue avant de produire quoi que ce soit n'insère rien dans
 * `app.extracted_fields`. Il n'y a donc aucune cellule à qualifier, et
 * `extraction_failed` n'a rien sur quoi s'appliquer — mesuré sur staging, les 9
 * passes `failed` ont toutes zéro champ.
 *
 * Synthétiser des lignes de matrice pour l'occasion aurait créé des lignes
 * fantômes existant uniquement parce qu'une passe a échoué. Décision PO : un
 * bandeau au niveau de la soumission, et aucune ligne inventée.
 */
export const ComparisonFailedPassSchema = z.object({
  pass_id: z.string(),
  /** Code d'échec structuré (`EXTRACTION_TIMEOUT`, `EXTRACTION_STALE_REAPED`…). */
  error_code: z.string().nullable(),
  completed_at: z.string().nullable(),
});
export type ComparisonFailedPass = z.infer<typeof ComparisonFailedPassSchema>;

export const ComparisonSubmissionSchema = z.object({
  id: UuidSchema,
  insurer_code: z.string(),
  insurer_name: z.string(),
  // Statut submission (app.submission_status) — display-only, pas de couplage enum.
  status: z.string(),
  open_anomaly_count: z.number().int().nonnegative(),
  /**
   * 9.AB.1 — passes métier sans résultat exploitable. Vide dans le cas nominal.
   * Une passe qui a fini par réussir n'y figure PAS : seule la dernière tentative
   * compte, sinon un échec ancien resterait affiché pour toujours.
   */
  failed_passes: z.array(ComparisonFailedPassSchema),
});
export type ComparisonSubmission = z.infer<typeof ComparisonSubmissionSchema>;

// -----------------------------------------------------------------------------
// Cellule — valeur extraite consolidée d'un field_key pour une submission
// -----------------------------------------------------------------------------
//
// `value` est jsonb polymorphe (number, string, bool, object) — déjà consolidé
// par `consolidate_field_consensus` (0018), aucun recalcul Phase 1.

/** Les cinq significations d'une cellule — 9.AB.1, décision PO. */
export const CELL_STATUSES = [
  "value",
  "not_offered",
  "not_detected",
  // 9.AM.1 — le document est muet : toutes les lectures ont rapporté ce champ vide.
  "not_provided",
  "needs_review",
  "extraction_failed",
] as const;

export const CELL_STATUS_REASONS = [
  "usable_value",
  "explicit_negative_mention",
  "no_value_no_evidence",
  "reported_empty_by_all",
  "low_confidence_or_missing_citation",
  "unrecognized_categorical_value",
  "pass_failed",
] as const;

/**
 * Provenance d'une preuve négative — 9.AB.1.
 *
 * `citation_page` est le SEUL champ `citation_*` exposé sur ce wire, et c'est
 * délibéré : la décision PO exige « avec provenance/citation disponible » pour
 * retenir une preuve, et un conseiller doit pouvoir aller vérifier la page. Le
 * verbatim (`citation_quote`) reste interdit — on n'expose qu'un booléen disant
 * qu'il existe. La règle anti-leak de 9.Q.1 est donc élargie d'un numéro de page,
 * pas de contenu documentaire.
 */
export const ComparisonEvidenceSchema = z.object({
  field_key: z.string(),
  guarantee_family: z.string(),
  mention: z.string(),
  citation_page: z.number().int().nullable(),
  has_citation_quote: z.boolean(),
  /**
   * 9.AH.1 — la provenance retenue est-elle une CORRECTION HUMAINE ?
   *
   * Une correction arrive sans page ni citation : sans ce drapeau, la preuve
   * négative semblerait ne reposer sur rien alors qu'elle repose sur la décision
   * d'un courtier.
   */
  human_override: z.boolean(),
});
export type ComparisonEvidence = z.infer<typeof ComparisonEvidenceSchema>;

export const ComparisonCellSchema = z.object({
  value: z.unknown().nullable(),
  unit: z.string().nullable(),
  confidence: z.number().nullable(),
  agreement: z.number().nullable(),
  /**
   * CONSERVÉ à l'identique — décision PO : aucun resserrement silencieux de son
   * sens dans ce chunk, ses cinq consommateurs continuent de fonctionner. Voir
   * `cell_status` pour le signal principal.
   */
  needs_review: z.boolean(),
  /**
   * 9.AL.1 — POURQUOI la revue est demandée, en motifs nommés et cohérents avec
   * le booléen ci-dessus. `null` = motif non calculé (ligne antérieure), jamais
   * « aucun motif ». Conseiller uniquement : le rapport client n'en voit rien.
   */
  needs_review_reasons: z.array(z.string()).nullable(),
  has_open_anomaly: z.boolean(),
  /** 9.AB.1 — signal PRINCIPAL, calculé côté serveur. */
  cell_status: z.enum(CELL_STATUSES),
  status_reason: z.enum(CELL_STATUS_REASONS),
  /** Non-null uniquement sur `not_offered`. */
  evidence: ComparisonEvidenceSchema.nullable(),
  /**
   * 9.AI.1 — la valeur de CETTE cellule vient-elle d'une correction humaine ?
   *
   * Distinct de `evidence.human_override`, qui ne parle que d'une preuve négative
   * portée par un champ frère. Un booléen seulement : `override_by` reste interne.
   */
  is_human_override: z.boolean(),
  /**
   * 9.AK.1 — qualification OBSERVATIONNELLE de la preuve : sur quoi cette valeur
   * repose (combien de lectures, de combien de modèles distincts).
   *
   * Volontairement DISTINCTE de `needs_review`, de `cell_status` et du score
   * d'assureur. `null` = pas encore qualifiée — jamais « aucun consensus ».
   */
  consensus: z
    .object({
      state: z.string(),
      model_count: z.number().int(),
      candidate_count: z.number().int(),
    })
    .nullable(),
  /**
   * Niveau d'hébergement `1 | 2 | 3` (publique < semi-privée < privée), uniquement
   * sur `amc.hospital_room`. `null` ailleurs, et `null` aussi pour une valeur non
   * reconnue — qui passe alors en vérification plutôt qu'être classée arbitrairement.
   */
  hospital_room_level: z.number().int().min(1).max(3).nullable(),
});
export type ComparisonCell = z.infer<typeof ComparisonCellSchema>;

// -----------------------------------------------------------------------------
// Ligne — un field_key + ses cellules par submission_id
// -----------------------------------------------------------------------------
//
// `cells` est un Record submission_id → cellule. Une submission ABSENTE de la
// map = pas de valeur pour ce field_key (cellule vide gracieuse côté UI).

/** Nature d'un champ vis-à-vis de la comparaison — registre shared authoritative. */
export const FIELD_COMPARABILITIES = [
  "guarantee",
  "document_metadata",
  "technical",
  "unknown",
] as const;

export const ROW_HIDDEN_REASONS = [
  "document_metadata",
  "technical_field",
  "empty_for_all_insurers",
] as const;

export const ComparisonRowSchema = z.object({
  field_key: z.string(),
  /**
   * 9.AB.1 — classification et masquage viennent du SERVEUR. Décision PO : pas de
   * liste divergente cachée dans le composant React.
   */
  comparability: z.enum(FIELD_COMPARABILITIES),
  /** Famille de garantie déclarée au contrat, ou `null` pour un champ plat. */
  guarantee_family: z.string().nullable(),
  hidden_by_default: z.boolean(),
  /** Toujours renseigné quand la ligne est masquée — jamais de masquage muet. */
  hidden_reason: z.enum(ROW_HIDDEN_REASONS).nullable(),
  cells: z.record(UuidSchema, ComparisonCellSchema),
});
export type ComparisonRow = z.infer<typeof ComparisonRowSchema>;

// -----------------------------------------------------------------------------
// Réponse — GET /v1/projects/:id/comparison
// -----------------------------------------------------------------------------

export const ComparisonResponseSchema = z.object({
  project_id: UuidSchema,
  // Filtre appliqué (null = projet complet).
  quote_request_id: UuidSchema.nullable(),
  submissions: z.array(ComparisonSubmissionSchema),
  rows: z.array(ComparisonRowSchema),
  /**
   * Nombre de lignes masquées par défaut — permet à l'interface d'annoncer
   * « Afficher les détails et les lignes masquées (N) » sans recompter, et à un
   * consommateur non-UI de savoir immédiatement qu'il ne voit pas tout.
   */
  hidden_row_count: z.number().int().nonnegative(),
  // ---------------------------------------------------------------------------
  // Chunk 9.AH.1 — la couche de RÉCEPTION
  // ---------------------------------------------------------------------------
  //
  // Un niveau AU-DESSUS de la matrice, jamais mêlé aux cellules.
  //
  // `awaiting`, `declined` et `expired` n'ont AUCUNE entrée dans `submissions` ni
  // dans `rows` : une colonne vide se lirait « aucune garantie offerte ». Ils
  // apparaissent ICI, ce qui est la seule façon de distinguer « les cinq ont
  // répondu » de « deux ont répondu, trois se taisent ».
  //
  // Optionnel dans le schéma : un consommateur écrit avant ce chunk ne doit pas
  // casser à la lecture d'une charge enrichie.
  insurers: z
    .array(
      z.object({
        recipient_id: UuidSchema,
        quote_request_id: UuidSchema,
        insurer_code: z.string(),
        recipient_status: z.string(),
        reception_state: z.enum(Comparison.RECEPTION_STATES),
        submission_id: UuidSchema.nullable(),
        /** Faux quand la proposition existe mais n'est pas dans CETTE matrice. */
        has_column: z.boolean(),
      }),
    )
    .optional(),
  awaiting_count: z.number().int().nonnegative().optional(),
  declined_count: z.number().int().nonnegative().optional(),
  comparable_count: z.number().int().nonnegative().optional(),
});
export type ComparisonResponse = z.infer<typeof ComparisonResponseSchema>;

// -----------------------------------------------------------------------------
// Query — filtre optionnel par quote_request_id
// -----------------------------------------------------------------------------

export const ComparisonQuerySchema = z.object({
  quote_request_id: UuidSchema.optional(),
});
export type ComparisonQuery = z.infer<typeof ComparisonQuerySchema>;
